import React from "react";
import {
  Grid,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import InfoIcon from "@material-ui/icons/Info";
import { useForm, Controller, FormContext } from "react-hook-form";
import CustomSnackbar from "../../../components/notifications/CustomSnackbar";
import {
  getNaccasReportSetting,
  SaveOrUpdateNaccasReportSetting,
  getNaccasDropReasons,
} from "../../../api/reports/stateBoard/stateBoardReportApi";
import EnrollmentStatusAutoComplete from "../../../components/AutoComplete/EnrollmentStatusAutoComplete";
import ProgramVersionAutoComplete from "../../../components/AutoComplete/ProgramVersionsMCAutoComplete";
import DropReasonAutoComplete from "../../../components/AutoComplete/DropReasonAutoComplete";
import NaccasDropReasonMapping from "./NaccasDropReasonMapping";

import { Control } from "react-hook-form";
type UseFormRegister<T = any> = any;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      padding: theme.spacing(4),
      backgroundColor: "#f7f7f7",
      minHeight: "100vh",
    },
    section: {
      marginBottom: theme.spacing(4),
      padding: theme.spacing(2),
      backgroundColor: "#ffffff",
      borderRadius: theme.shape.borderRadius,
    },
    formControl: {
      width: "100%",
    },
    buttonGroup: {
      marginTop: theme.spacing(3),
    },
    reasonHeader: {
      display: "flex",
      alignItems: "center",
      marginBottom: theme.spacing(1),
    },
    reasonCode: {
      backgroundColor: theme.palette.grey[200],
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      marginLeft: theme.spacing(1),
      fontSize: "0.8rem",
      fontWeight: 700,
    },
    exemptReason: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
    },
    searchField: {
      margin: theme.spacing(1, 0, 2),
    },
    tooltipIcon: {
      marginLeft: theme.spacing(1),
      fontSize: "1.2rem",
      color: theme.palette.primary.main,
    },
  })
);

interface IProps {
  campusId: string;
  agencyId: string;
  agencyDescription: string;
}

type DropMapping = {
  naccasReasonId: string;
  advantageReasons: string[];
};

type NaccasSettingsPayload = {
  campusId: string;
  allowGraduateAndOweMoney: boolean;
  allowGraduateWithoutFullCompletion: boolean;
  approvedProgramVersions: string[];
  dropReasonMappings: DropMapping[];
  naccasGraduateStatuses: string[];
};

interface NaccasSettingsModel {
  campusId: string[];
  enrollmentStatuses: string[];
  programVersions: string[];
  statusCodeId: string | null;
  dropReasonId: string | null;
  allowGraduateAndOweMoney: boolean;
  allowGraduateWithoutFullCompletion: boolean;
  approvedProgramVersions: string[];
  dropReasonMappings: DropMapping[];
  naccasGraduateStatuses: string[];
  fundSources?: string[];
  [key: string]: any;
}

interface NaccasReason {
  id: string;
  label: string;
}

interface AdvantageReason {
  id: string;
  label: string;
}

interface SearchTerms {
  [reasonId: string]: string;
}

const NaccasAgencyReportSetup = (props: IProps) => {
  const classes = useStyles();
  const form = useForm<NaccasSettingsPayload>({
    defaultValues: {
      campusId: props.campusId,
      allowGraduateAndOweMoney: false,
      allowGraduateWithoutFullCompletion: false,
      approvedProgramVersions: [],
      dropReasonMappings: [],
      naccasGraduateStatuses: [],
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    register,
    unregister,
  } = form;

  React.useEffect(() => {
    register("dropReasonMappings");

    return () => {
      unregister("dropReasonMappings");
    };
  }, [register, unregister]);

  const [model, setModel] = React.useState<NaccasSettingsModel>({
    campusId: props.campusId ? [props.campusId] : [],
    enrollmentStatuses: [],
    programVersions: [],
    statusCodeId: null,
    dropReasonId: null,
    allowGraduateAndOweMoney: false,
    allowGraduateWithoutFullCompletion: false,
    approvedProgramVersions: [],
    dropReasonMappings: [],
    naccasGraduateStatuses: [],
  });

  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    variant: "info",
  });

  const [naccasDropReasons, setNaccasDropReasons] = React.useState<
    NaccasReason[]
  >([]);
  const [advantageDropReasons, setAdvantageDropReasons] = React.useState<
    AdvantageReason[]
  >([]);

  const [enrollmentStatusError, setEnrollmentStatusError] = React.useState<string | null>(null);

  const onSubmit = async (data: NaccasSettingsPayload) => {
    if (!model.enrollmentStatuses || model.enrollmentStatuses.length === 0) {
      setEnrollmentStatusError("NACCAS Graduate Status(es) is required");
      setSnackbar({
        open: true,
        message: "Please select at least one graduate status",
        variant: "error",
      });
      return;
    }
    
    setIsSaving(true);
    try {
      const payload = {
        ...data,
        campusId: props.campusId,
        approvedProgramVersions: model.programVersions || [],
        dropReasonMappings: model.dropReasonMappings,
        naccasGraduateStatuses: model.enrollmentStatuses || [],
      };

      await SaveOrUpdateNaccasReportSetting(payload);
      setSnackbar({
        open: true,
        message: "Saved successfully",
        variant: "success",
      });
    } catch (err) {
      console.error("Save error:", err);
      setSnackbar({ open: true, message: "Save failed", variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const dataLoaded = React.useRef(false);
  const lastLoadedCampusId = React.useRef<string | null>(null);

  const loadSettings = React.useCallback(async () => {
    if (dataLoaded.current && lastLoadedCampusId.current === props.campusId)
      return;

    setIsLoading(true);
    try {
      lastLoadedCampusId.current = props.campusId;

      const res = await getNaccasReportSetting(props.campusId);

      setModel({
        ...res,
        campusId: props.campusId ? [props.campusId] : [],
        enrollmentStatuses: res.naccasGraduateStatuses || [],
        programVersions: res.approvedProgramVersions || [],
        dropReasonId: res.dropReasonId || null,
      });

      reset({
        campusId: props.campusId,
        allowGraduateAndOweMoney: res.allowGraduateAndOweMoney || false,
        allowGraduateWithoutFullCompletion:
          res.allowGraduateWithoutFullCompletion || false,
        naccasGraduateStatuses: res.naccasGraduateStatuses || [],
        dropReasonMappings: res.dropReasonMappings || [],
      });

      dataLoaded.current = true;
    } catch (err) {
      console.error("Failed to load settings:", err);
      setSnackbar({
        open: true,
        message: "Failed to load settings",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [props.campusId, reset]);

  React.useEffect(() => {
    if (lastLoadedCampusId.current === props.campusId) {
      return;
    }

    dataLoaded.current = false;

    setModel((prevModel) => ({
      ...prevModel,
      campusId: props.campusId ? [props.campusId] : [],
    }));

    loadSettings();
  }, [props.campusId, loadSettings]);

  const handleFieldOnChange = React.useCallback(
    (
      fieldId: string,
      value: any,
      mapFunction?: (values: any, key: string) => Array<string>
    ) => {
      const newValue = mapFunction ? mapFunction(value, "value") : value;
      setModel((prevModel: NaccasSettingsModel) => ({
        ...prevModel,
        [fieldId]: newValue,
      }));

      if (fieldId === "enrollmentStatuses") {
        setValue("naccasGraduateStatuses", newValue, false);
        if (newValue && newValue.length > 0) {
          setEnrollmentStatusError(null);
        }
      } else if (fieldId === "programVersions") {
        setValue("approvedProgramVersions", newValue, false);
      }
    },
    [setValue]
  );

  const setArrayValues = (values: any, key: string) => {
    let newValues: string[] = [];
    if (values !== null) {
      if (Array.isArray(values)) {
        newValues = values.map((gr: any) => gr[key]);
      } else {
        newValues.push(values.value);
      }
    }
    return newValues;
  };

  const handleDropReasonMappingsChange = React.useCallback(
    (mappings: DropMapping[]) => {
      const validMappings = mappings.filter(
        (m) => m.advantageReasons && m.advantageReasons.length > 0
      );
      setValue("dropReasonMappings", validMappings);
      setModel((prev) => ({
        ...prev,
        dropReasonMappings: validMappings,
      }));
    },
    [setValue, setModel]
  );
  return (
    <FormContext {...form}>
      <div className={classes.wrapper}>
        <Typography variant="h5" gutterBottom>
          NACCAS Report Settings – {props.agencyDescription}
        </Typography>

        <form
          onSubmit={(e) => {
            handleSubmit(onSubmit)(e);
          }}
        >
          <div className={classes.section}>
            <FormControl className={classes.formControl}>
              <Grid item md={6} sm={6} xs={12}>
                {React.useMemo(
                  () => (
                    <EnrollmentStatusAutoComplete
                      id="enrollmentStatuses"
                      name="enrollmentStatuses"
                      chipSize="small"
                      maxTags={2}
                      multiple={true}
                      filterHandle={(statuses: any) => {
                        handleFieldOnChange(
                          "enrollmentStatuses",
                          statuses,
                          setArrayValues
                        );
                      }}
                      label="NACCAS Graduate Status(es) *"
                      required={true}
                      error={!!enrollmentStatusError}
                      helperText={enrollmentStatusError}
                      valueFilter={
                        model?.enrollmentStatuses &&
                        model.enrollmentStatuses.length > 0
                          ? {
                              key: "value",
                              values: model.enrollmentStatuses,
                            }
                          : undefined
                      }
                    />
                  ),
                  [
                    model.enrollmentStatuses,
                    model.campusId,
                    handleFieldOnChange,
                  ]
                )}
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                {React.useMemo(
                  () => (
                    <ProgramVersionAutoComplete
                      showIncludeAllValue={
                        model.campusId && model.campusId.length > 0
                      }
                      includeAllValue={
                        model.campusId && model.campusId.length > 0
                      }
                      chipSize="small"
                      maxTags={2}
                      label="Approved Program Version(s)"
                      name="programVersions"
                      campuses={model.campusId}
                      showInactivePrograms={true}
                      filterHandle={(value: any) =>
                        handleFieldOnChange(
                          "programVersions",
                          value,
                          setArrayValues
                        )
                      }
                      valueFilter={
                        model.programVersions
                          ? {
                              key: "value",
                              values: model.programVersions,
                            }
                          : undefined
                      }
                      multiple={true}
                    />
                  ),
                  [model.campusId, model.programVersions, handleFieldOnChange]
                )}
              </Grid>
            </FormControl>
          </div>

          <NaccasDropReasonMapping
            campusId={props.campusId}
            initialMappings={model.dropReasonMappings}
            onMappingsChange={handleDropReasonMappingsChange}
          />

          {isLoading && <CircularProgress />}

          <Grid container spacing={2} className={classes.buttonGroup}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? <CircularProgress size={24} /> : "Save"}
              </Button>
            </Grid>
            {/* <Grid item>
              <Button variant="outlined" onClick={() => form.reset()}>
                Reset
              </Button>
            </Grid> */}
          </Grid>
        </form>

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          variant={snackbar.variant as any}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        />
      </div>
    </FormContext>
  );
};

export default NaccasAgencyReportSetup;
